import React, { useState } from 'react';
import ActionDialog from 'common/components/ActionDialog';
import {
	FormControl,
	FormControlLabel,
	RadioGroup,
	Radio,
	TextField,
	Typography,
	Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { DepositType, CurrencyObject } from 'common/types';
import MoneyTextField from 'common/components/MoneyTextField';
import { useTranslation } from 'services/localization/useTranslation';
import { DepositPricing } from 'common/modules/atoms/pricing';

interface Props {
	deposit: DepositPricing;
	currency: CurrencyObject;
	onSave: (data: DepositPricing) => void;
	onCancel: () => void;
}

const getReturnedSaveParams = (
	type: DepositType,
	value: number,
	additionalInfo: string,
): DepositPricing => {
	return type === 'payment' && value === 0
		? {
				type: 'no-deposit',
				value: 0,
				additionalInfo,
		  }
		: type === 'payment'
		? {
				type,
				value,
		  }
		: {
				type,
				additionalInfo,
				value: 0,
		  };
};

const DepositSelectDialog = (props: Props) => {
	const { onSave, onCancel, currency, deposit } = props;
	const [depositType, setDepositType] = useState<DepositType>(deposit.type || 'payment');
	const [amount, setAmount] = useState<number>(deposit.value || 0);
	const [additionalInfo, setAdditionalInfo] = useState<string>(deposit.additionalInfo || '');
	const classes = useStyles();
	const { t } = useTranslation();

	return (
		<ActionDialog
			title={t('payment.depositType', 'Select deposit')}
			btnActions={{
				primary: {
					text: t('common:actions.save'),
					onClick: () => onSave(getReturnedSaveParams(depositType, amount, additionalInfo)),
				},
				secondary: {
					text: t('common:actions.cancel'),
					onClick: onCancel,
				},
			}}
			onClose={onCancel}
		>
			<FormControl component="div" fullWidth>
				<RadioGroup
					value={depositType}
					onChange={(e) => setDepositType(e.target.value as DepositType)}
				>
					<FormControlLabel
						className={classes.formControlLabel}
						classes={{
							label: classes.radioLabel,
						}}
						value="payment"
						control={<Radio className={classes.radio} color="primary" />}
						label={
							<>
								<Typography variant="body1" color="textPrimary">
									{t('common:payment.money', 'Money')}
								</Typography>
								{depositType === 'payment' && (
									<MoneyTextField
										variant="standard"
										currency={currency}
										label={t('common:payment.amount', 'Amount')}
										fullWidth
										margin="normal"
										value={amount}
										onValueChange={(value) => setAmount(value)}
										InputLabelProps={{
											shrink: true,
										}}
										useMinorUnits={true}
									/>
								)}
							</>
						}
					/>
					<FormControlLabel
						className={classes.formControlLabel}
						classes={{
							label: classes.radioLabel,
						}}
						value="other"
						control={<Radio color="primary" className={classes.radio} />}
						label={
							<>
								<Typography variant="body1" color="textPrimary">
									{t('common:payment.item', 'Item')}
								</Typography>
								{depositType === 'other' && (
									<TextField
										label={t('common:form.description', 'Description')}
										value={additionalInfo}
										onChange={(e) => setAdditionalInfo(e.target.value)}
										margin="normal"
										fullWidth
										InputLabelProps={{
											shrink: true,
										}}
									/>
								)}
							</>
						}
					/>
					<FormControlLabel
						className={classes.formControlLabel}
						classes={{
							label: classes.radioLabel,
						}}
						value="no-deposit"
						control={<Radio color="primary" className={classes.radio} />}
						label={
							<Typography variant="body1" color="textPrimary">
								{t('common:payment.noDeposit', 'No deposit')}
							</Typography>
						}
					/>
				</RadioGroup>
			</FormControl>
		</ActionDialog>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		formControlLabel: {
			alignItems: 'flex-start',
			padding: theme.spacing(1, 0),
		},
		radio: {
			padding: 0,
			marginLeft: 9,
			marginRight: 13,
		},
		radioLabel: {
			flex: 1,
		},
	}),
);

export default DepositSelectDialog;
